// The ByteDance art (tools/art/isoart.py -> client/public/art/iso): textures the renderer samples per pixel
// (walls, ground, facade pieces), sprites it places (props, trees, cars), and item icons (the pack, held and
// worn gear). Everything is optional: a missing frame falls back to the procedural painter.
import { Assets, Rectangle, Texture } from 'pixi.js';
import { gradeFrame, carPaint, gradeTex, synthTex } from './style_grade';
import { ART_B, loadKtx } from './paintblock';

export interface Frame { page: string; x: number; y: number; w: number; h: number; wm?: number; dm?: number; zm?: number }
export interface Pix { w: number; h: number; d: Uint8ClampedArray }

/** studio 009p: the repainted car views' byte cap (RGBA canvases; a painted sedan view is ~0.4 MB) and how long one must
 *  go unasked before it can be given back */
const CAR_CAP = 8 * 1048576, CAR_IDLE_MS = 5000;

export class IsoArt {
  atlas: any = null;
  pix = new Map<string, Pix>();             // tex + flat frames as pixels
  tex = new Map<string, Texture>();         // iso + icon frames as textures
  pages = new Map<string, Texture>();
  carCache = new Map<string, Texture>();
  ok = false;
  /** studio 009p (the painted look only): the painted street props and cars (tools/art/props_b.py -> atlas_b.json), drawn
   *  at 1/res through their page's texture resolution, so every frame keeps the old atlas units and nothing that places
   *  one changes. name -> its frame (old units) and page; the recoloured cars' page is a CPU image, never uploaded */
  bframes = new Map<string, { page: string; x: number; y: number; w: number; h: number }>();
  bimg = new Map<string, HTMLImageElement>();
  bres = 1; bBytes = 0; bFailed = 0;
  /** the repainted car views: last use and bytes, so the cache is an LRU under CAR_CAP (painted views are ~16x the texels) */
  carUse = new Map<string, { at: number; bytes: number }>(); carBytes = 0;

  /** pages loaded and to load, for the boot screen's counter (the three playtests of 2026-10-28 read a still
   *  'Unpacking the art...' as a tap that did nothing) */
  static done = 0; static total = 0;
  async load(base = 'art/iso/') {
    try {
      const r = await fetch(base + 'atlas.json'); if (!r.ok) return;
      this.atlas = await r.json();
    } catch { return; }
    // studio 008: the records room's furniture, its own page (tools/echo/install_props.py), merged over the main atlas
    try { const e = await fetch(base + 'atlas_echo.json'); if (e.ok) Object.assign(this.atlas.iso ??= {}, (await e.json()).iso ?? {}); } catch { /* optional */ }
    const pageNames = new Set<string>();
    for (const k of ['tex', 'flat', 'iso', 'icons']) for (const f of Object.values(this.atlas[k] ?? {}) as Frame[]) pageNames.add(f.page);
    const imgs = new Map<string, HTMLImageElement>();
    IsoArt.total += pageNames.size;
    await Promise.all([...pageNames].map(async (pn) => {
      const img = new Image(); img.src = base + pn; await img.decode().catch(() => {});
      imgs.set(pn, img);
      this.pages.set(pn, await Assets.load<Texture>(base + pn)); IsoArt.done++;
    }));
    // pixel access for the samplers
    const cv = document.createElement('canvas'); const g = cv.getContext('2d', { willReadFrequently: true })!;
    for (const k of ['tex', 'flat']) for (const [name, f] of Object.entries(this.atlas[k] ?? {}) as [string, Frame][]) {
      const img = imgs.get(f.page); if (!img) continue;
      cv.width = f.w; cv.height = f.h; g.clearRect(0, 0, f.w, f.h); g.drawImage(img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
      const pd = g.getImageData(0, 0, f.w, f.h).data;
      if (k === 'tex') gradeTex(name, pd);                  // STYLE H10: calm a swatch louder than its neighbours
      this.pix.set((k === 'flat' ? 'flat:' : '') + name, { w: f.w, h: f.h, d: pd });
    }
    // STYLE H10: grade the sprite pages once (defringe the keying, per-family saturation ceilings) before any texture
    // is cut from them; a page that cannot be read back keeps its original pixels
    const isoPages = new Set((Object.values(this.atlas.iso ?? {}) as Frame[]).map(f => f.page));
    for (const pn of isoPages) {
      const img = imgs.get(pn); if (!img || !img.naturalWidth) continue;
      try {
        const pc = document.createElement('canvas'); pc.width = img.naturalWidth; pc.height = img.naturalHeight;
        const pg = pc.getContext('2d', { willReadFrequently: true })!; pg.drawImage(img, 0, 0);
        const id = pg.getImageData(0, 0, pc.width, pc.height);
        for (const [name, f] of Object.entries(this.atlas.iso) as [string, Frame][]) if (f.page === pn) gradeFrame(id.data, pc.width, f.x, f.y, f.w, f.h, name);
        if (ART_B) for (const [, f] of Object.entries(this.atlas.iso) as [string, Frame][]) if (f.page === pn && (f.zm ?? 1) >= 2.4) footArc(id.data, pc.width, f);   // studio 004 (Matt, 2026-10-20)
        pg.putImageData(id, 0, 0);
        const t = Texture.from(pc); t.source.scaleMode = 'nearest';
        this.pages.set(pn, t);
      } catch { /* keep the ungraded page */ }
    }
    // STYLE H10: wall materials the atlas has no swatch for get a synthesised one (glass), never a flat face colour
    for (const name of ['walls:glass']) if (!this.pix.has(name)) { const t = synthTex(name); if (t) this.pix.set(name, t); }
    for (const k of ['iso', 'icons']) for (const [name, f] of Object.entries(this.atlas[k] ?? {}) as [string, Frame][]) {
      const pt = this.pages.get(f.page); if (!pt) continue;
      this.tex.set((k === 'icons' ? 'icon:' : '') + name, new Texture({ source: pt.source, frame: new Rectangle(f.x, f.y, f.w, f.h) }));
    }
    if (ART_B) await this.loadB(base);
    if (new URLSearchParams(location.search).get('propcheck') === '1') (window as any).__isoArt = this;   // dev: tools/studio/props_b_check.mjs
    this.ok = true;
  }
  /** studio 009p: the painted pages over the atlas frames they replace (ART_B only; a page that fails keeps the old frames) */
  private async loadB(base: string) {
    let B: any;
    try { const r = await fetch(base + 'atlas_b.json'); if (!r.ok) return; B = await r.json(); } catch { return; }
    const res = this.bres = B.res ?? 4;
    IsoArt.total += Object.keys(B.pages).length;
    await Promise.all(Object.entries(B.pages as Record<string, any>).map(async ([pn, P]) => {
      try {
        if (P.recolour) {   // read on the CPU by car() only
          const img = new Image(); img.src = base + P.png; await img.decode(); this.bimg.set(pn, img);
        } else {
          let t: Texture;
          try { t = await loadKtx(base + P.ktx2); t.source.alphaMode = 'premultiplied-alpha'; this.bBytes += t.source.pixelWidth * t.source.pixelHeight * (/rgba8/.test(String(t.source.format)) ? 4 : 1); }
          catch { t = await Assets.load<Texture>(base + P.png); this.bBytes += t.source.pixelWidth * t.source.pixelHeight * 4; }
          t.source.resolution = res; t.source.scaleMode = 'linear';
          for (const [name, f] of Object.entries(B.iso) as [string, any][]) if (f.page === pn)
            this.tex.set(name, new Texture({ source: t.source, frame: new Rectangle(f.x, f.y, f.w, f.h) }));
        }
        for (const [name, f] of Object.entries(B.iso) as [string, any][]) if (f.page === pn) this.bframes.set(name, f);
      } catch { this.bFailed++; }
      IsoArt.done++;
    }));
    (window as any).__propsB = () => this.readoutB();   // the perf readout's painted-props line (?perf=1)
  }
  /** the perf readout's line for the painted props (GPU MB of the drawn page, repainted car views, failures) */
  readoutB() { return this.bframes.size ? `props b ${this.bframes.size} ${(this.bBytes / 1048576).toFixed(1)}MB cars ${this.carCache.size} ${(this.carBytes / 1048576).toFixed(1)}MB fail ${this.bFailed}` : ''; }
  frame(name: string): Frame | undefined { return this.atlas?.iso?.[name]; }
  icon(id: string): Texture | undefined { return this.tex.get('icon:' + id); }
  /** sample a texture at (u, v) in its own pixels, wrapping; null when transparent or missing */
  sample(key: string, u: number, v: number): [number, number, number] | null {
    const p = this.pix.get(key); if (!p) return null;
    const x = ((Math.floor(u) % p.w) + p.w) % p.w, y = ((Math.floor(v) % p.h) + p.h) % p.h;
    const o = (y * p.w + x) * 4;
    return p.d[o + 3] < 128 ? null : [p.d[o], p.d[o + 1], p.d[o + 2]];
  }
  /** sample a flat facade piece by fractions (0..1 across, 0 = bottom .. 1 = top) */
  flat(key: string, fu: number, fz: number): [number, number, number] | null {
    const p = this.pix.get('flat:' + key); if (!p) return null;
    if (fu < 0 || fu >= 1 || fz < 0 || fz >= 1) return null;
    return this.sample('flat:' + key, fu * p.w, (1 - fz) * p.h);
  }
  /** a car view, repainted in one of the colours (the art is painted mid grey; its grey body takes the colour) */
  car(model: string, view: string, colour: string | null): Texture | undefined {
    const name = `${model}_${view}`;
    const bf = this.bframes.get(name), bimg = bf && this.bimg.get(bf.page);   // studio 009p: a painted view, repainted from the CPU page
    const base = this.tex.get(name); if (!base && !bimg) return undefined;
    if (!bimg && (!colour || model === 'police' || model === 'van')) return base;
    const key = name + (colour ?? '');
    const hit = this.carCache.get(key); if (hit) { const u = this.carUse.get(key); if (u) u.at = performance.now(); return hit; }
    const R = bimg ? this.bres : 1;
    const f = bimg ? bf! : this.atlas.iso[name] as Frame;
    const img = bimg ?? ((this.pages.get(f.page)!.source.resource as any) as CanvasImageSource);
    const cv = document.createElement('canvas'); cv.width = f.w * R; cv.height = f.h * R;
    const g = cv.getContext('2d')!; g.drawImage(img, f.x * R, f.y * R, f.w * R, f.h * R, 0, 0, f.w * R, f.h * R);
    if (colour) {
      const id = g.getImageData(0, 0, cv.width, cv.height), d = id.data;
      const c = [parseInt(colour.slice(1, 3), 16), parseInt(colour.slice(3, 5), 16), parseInt(colour.slice(5, 7), 16)];
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 128) continue;
        const r = d[i], gg = d[i + 1], b = d[i + 2];
        const mx = Math.max(r, gg, b), mn = Math.min(r, gg, b), l = (r + gg + b) / 3;
        if (mx - mn < 22 && l > 55 && l < 215) {           // the grey paint (not the dark glass, tyres or chrome highlights)
          const k = l / 128;
          const [pr, pg2, pb] = carPaint(Math.min(255, c[0] * k), Math.min(255, c[1] * k), Math.min(255, c[2] * k));   // STYLE H10
          d[i] = pr; d[i + 1] = pg2; d[i + 2] = pb;
        }
      }
      g.putImageData(id, 0, 0);
    }
    let t = Texture.from(cv);
    if (R > 1) { t.source.resolution = R; t.source.scaleMode = 'linear'; t = new Texture({ source: t.source }); }   // drawn at 1/res: the old frame's size and anchor
    this.carCache.set(key, t);
    const bytes = cv.width * cv.height * 4; this.carUse.set(key, { at: performance.now(), bytes }); this.carBytes += bytes;
    this.trimCars();
    return t;
  }
  /** studio 009p: the repainted views over CAR_CAP give back the ones no car has asked for in CAR_IDLE_MS (car() is asked
   *  every frame for every car in view, so an idle entry is on no sprite) - their canvas and GPU texture destroyed */
  private trimCars() {
    if (this.carBytes <= CAR_CAP) return;
    const now = performance.now();
    for (const [k, u] of [...this.carUse].sort((a, b) => a[1].at - b[1].at)) {
      if (this.carBytes <= CAR_CAP * 0.8 || now - u.at < CAR_IDLE_MS) break;
      this.carCache.get(k)?.destroy(true); this.carCache.delete(k); this.carUse.delete(k); this.carBytes -= u.bytes;
    }
  }
}

/** studio 004, route b only (the default; not under ?art=a) (Matt, 2026-10-20: "some of the lamp bases were improperly cut compared to the sidewalk"): a tall
 *  pole sprite whose generated image was cropped straight through its round plinth ends in a flat horizontal line on the
 *  pavement. From the game's camera (30 degrees above) a round base's foot is the lower half of an ellipse half as tall as
 *  it is wide, so the bottom rows are carved to that arc (1 px soft edge). A sprite whose bottom row is already narrow (a
 *  foot, a point) is left alone. */
function footArc(d: Uint8ClampedArray, W: number, f: Frame) {
  const A = (x: number, y: number) => d[((f.y + y) * W + f.x + x) * 4 + 3];
  const span = (y: number) => { let l = -1, r = -1; for (let x = 0; x < f.w; x++) if (A(x, y) > 40) { if (l < 0) l = x; r = x; } return [l, r]; };
  let mx = 0; for (let y = 0; y < f.h; y++) { const [l, r] = span(y); if (l >= 0) mx = Math.max(mx, r - l + 1); }
  const bot = f.h - 1, [l, r] = span(bot);
  if (l < 0 || r - l + 1 < 0.6 * mx) return;
  const cx = (l + r) / 2, rx = (r - l + 1) / 2, ry = rx * 0.5;
  for (let x = l; x <= r; x++) {
    const u = Math.min(1, Math.abs(x - cx) / rx), lift = ry * (1 - Math.sqrt(1 - u * u));   // rows lost at this column
    for (let y = Math.max(0, Math.floor(bot - lift)); y <= bot; y++) {
      const keep = Math.min(1, Math.max(0, bot - lift - y + 1));          // 1 above the arc, a fraction on it, 0 below
      const o = ((f.y + y) * W + f.x + x) * 4 + 3; d[o] = Math.round(d[o] * keep);
    }
  }
}
